<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid'

const { t, locale } = useI18n()
const { submitCandidateRegistrationRequest, submitCandidateVerification } = useCandidate()
const localePath = useLocalePath()
const { countrySetting, updateCountrySetting } = useCountrySetting()
const { countryOptions } = useFrontendConfiguration()
const { candidateLogin } = useCandidate()
const { proxy: proxyMetaPixel } = useMetaPixel()

const emit = defineEmits({
  showSignIn: () => true,
})

function handleCountryChange(newCountry?: string) {
  updateCountrySetting(newCountry! as CountryType)
}

enum CandidateRegistrationStep {
  INITIATION,
  VERIFICATION,
}

const currentStep = ref(CandidateRegistrationStep.INITIATION)

const usedTelephoneNumbers = ref<string[]>([])
const invalidTelephoneNumbers = ref<string[]>([])
const telephoneNumber = ref('')
const telephoneNumberErrorMessage = ref<string | undefined>(undefined)
function validateTelephoneNumber() {
  telephoneNumberErrorMessage.value = requiredTelephoneNumberRule(
    telephoneNumber.value,
    t('Bitte gib eine gültige Mobilfunknummer ein.'))

  if (usedTelephoneNumbers.value.indexOf(telephoneNumber.value) > -1) {
    telephoneNumberErrorMessage.value = t('Diese Mobilfunknummer wird bereits verwendet.')
  }

  if (invalidTelephoneNumbers.value.indexOf(telephoneNumber.value) > -1) {
    telephoneNumberErrorMessage.value = t('Diese Mobilfunknummer ist ungültig.')
  }

  return !telephoneNumberErrorMessage.value
}

function resetRegistrationRequestForm() {
  telephoneNumber.value = ''
  telephoneNumberErrorMessage.value = undefined
  usedTelephoneNumbers.value = []
  invalidTelephoneNumbers.value = []
}

const submittingRequest = ref(false)

async function submitRequest() {
  if (validateAll([validateTelephoneNumber])) {
    submittingRequest.value = true

    try {
      const eventId = uuidv4()

      const result = await submitCandidateRegistrationRequest({
        telephoneNumber: telephoneNumber.value,
        locale: locale.value,
        timezone: guessTimezone(),
        country: countrySetting.value!,
      }, eventId)

      if (result === CandidateRegistrationRequestResult.TELEPHONE_NUMBER_ALREADY_IN_USE) {
        usedTelephoneNumbers.value.push(telephoneNumber.value)
        validateTelephoneNumber()
      }
      if (result === CandidateRegistrationRequestResult.INVALID_SMS_DESTINATION_NUMBER) {
        invalidTelephoneNumbers.value.push(telephoneNumber.value)
        validateTelephoneNumber()
      }
      if (result === CandidateRegistrationRequestResult.SUCCESS) {
        await proxyMetaPixel.fbq('trackCustom', 'CandidateRegistrationRequest', {}, { eventID: eventId })
        verificationTelephoneNumber.value = telephoneNumber.value
        resetRegistrationVerificationForm()
        resetRegistrationRequestForm()
        currentStep.value = CandidateRegistrationStep.VERIFICATION
      }
    }
    finally {
      submittingRequest.value = false
    }
  }
}

const verificationTelephoneNumber = ref('')

const invalidRequestCode = ref('')
const requestCode = ref('')
const requestCodeErrorMessage = ref<string | undefined>(undefined)
function validateRequestCode() {
  requestCodeErrorMessage.value = regExpRule(
    requestCode.value,
    /^[0-9]{6}$/,
    t('Bitte gib den Verifizierungscode ein.'))

  if (!!invalidRequestCode.value && requestCode.value === invalidRequestCode.value) {
    requestCodeErrorMessage.value = t('Ungültiger oder abgelaufener Verifizierungscode. Bitte gehe zurück und fordere einen neuen an.')
  }

  return !requestCodeErrorMessage.value
}

function handleRequestCodeUpdate(newValue: string) {
  if (newValue !== invalidRequestCode.value) {
    invalidRequestCode.value = ''
    validateRequestCode()
  }
}

const password = ref('')
const passwordErrorMessage = ref<string | undefined>(undefined)
function validatePassword() {
  passwordErrorMessage.value = passwordRule(
    password.value,
    t('Das Passwort muss zwischen 8 und 32 Zeichen lang sein und mindestens einen Buchstaben, eine Zahl und ein Sonderzeichen enthalten.'))

  return !passwordErrorMessage.value
}

const givenName = ref('')
const givenNameErrorMessage = ref<string | undefined>(undefined)
function validateGivenName() {
  givenNameErrorMessage.value = requiredRule(
    givenName.value,
    t('Bitte gib deinen Vornamen ein.'))

  return !givenNameErrorMessage.value
}

const familyName = ref('')
const familyNameErrorMessage = ref<string | undefined>(undefined)
function validateFamilyName() {
  familyNameErrorMessage.value = requiredRule(
    familyName.value,
    t('Bitte gib deinen Nachnamen ein.'))

  return !familyNameErrorMessage.value
}

const zip = ref('')
const zipErrorMessage = ref<string | undefined>(undefined)
function validateZip() {
  zipErrorMessage.value = regExpRule(
    zip.value,
    getZipCodeRegEx(countrySetting.value!)!,
    t('Bitte gib eine gültige Postleitzahl ein.'))

  return !zipErrorMessage.value
}

watch(countrySetting, () => {
  validateZip()
})

function resetRegistrationVerificationForm() {
  requestCode.value = ''
  requestCodeErrorMessage.value = undefined
  invalidRequestCode.value = ''
  password.value = ''
  passwordErrorMessage.value = undefined
  givenName.value = ''
  givenNameErrorMessage.value = undefined
  familyName.value = ''
  familyNameErrorMessage.value = undefined
  zip.value = ''
  zipErrorMessage.value = undefined
}

function goBack() {
  currentStep.value = CandidateRegistrationStep.INITIATION
}

const submittingVerification = ref(false)

async function submitVerification() {
  if (validateAll([validateRequestCode, validatePassword, validateGivenName, validateFamilyName, validateZip])) {
    submittingVerification.value = true

    try {
      const eventId = uuidv4()

      const result = await submitCandidateVerification({
        telephoneNumber: verificationTelephoneNumber.value,
        code: requestCode.value,
        password: password.value,
        givenName: givenName.value,
        familyName: familyName.value,
        zip: zip.value,
      }, eventId)

      if (result === CandidateVerificationResult.INVALID_CODE) {
        invalidRequestCode.value = requestCode.value
        validateRequestCode()
      }
      if (result === CandidateVerificationResult.SUCCESS) {
        await proxyMetaPixel.fbq('trackCustom', 'CandidateRegistration', {}, { eventID: eventId })
        await candidateLogin({ telephoneNumber: verificationTelephoneNumber.value, password: password.value })
        await navigateTo(localePath('candidatearea'))
      }
    }
    finally {
      submittingVerification.value = false
    }
  }
}
</script>

<template>
  <CustomCard
    :disabled="submittingRequest || submittingVerification"
    :title="currentStep === CandidateRegistrationStep.INITIATION ? t('Neues Profil erstellen') : t('Mobilfunknummer verifizieren')"
    title-size="2xl"
  >
    <div v-if="currentStep === CandidateRegistrationStep.INITIATION">
      <form @submit.prevent="submitRequest">
        <CustomInputSelect
          :model-value="countrySetting"
          class="mb-4"
          prepend-icon="pi-globe"
          :label="t('Wohnsitz')"
          :select-options="countryOptions"
          @update:model-value="handleCountryChange"
        />

        <CustomInputTelephoneNumber
          v-model="telephoneNumber"
          class="mb-4"
          :label="t('Mobilfunknummer')"
          autocomplete="tel"
          :error-message="telephoneNumberErrorMessage"
          :default-country-code="countrySetting!"
          @validate="validateTelephoneNumber"
        />

        <Button
          type="submit"
          :loading="submittingRequest"
          :label="t('Profil erstellen')"
          class="w-full mb-4"
        />

        <div>
          {{ t("Profil vorhanden?") }}
          <button
            class="font-medium text-primary"
            @click="emit('showSignIn')"
          >
            {{ t("Anmelden") }}
          </button>
        </div>
      </form>
    </div>

    <div v-if="currentStep === CandidateRegistrationStep.VERIFICATION">
      <div class="mb-4">
        {{ t("Bitte gib die folgenden Daten ein, um deine Profilerstellung abzuschließen.") }}
      </div>

      <form @submit.prevent="submitVerification">
        <CustomInputText
          prepend-icon="pi-mobile"
          :model-value="parseAndFormatNumber(verificationTelephoneNumber, 'INTERNATIONAL')!"
          disabled
          class="mb-4"
          :label="t('Mobilfunknummer')"
        />

        <CustomInputMask
          v-model="requestCode"
          autocomplete="one-time-code"
          class="mb-4"
          mask="999999"
          :label="t('Verifizierungscode')"
          :error-message="requestCodeErrorMessage"
          prepend-icon="pi-asterisk"
          @validate="validateRequestCode"
          @update:model-value="handleRequestCodeUpdate"
        />

        <CustomInputPassword
          v-model="password"
          class="mb-4"
          :label="t('Neues Passwort')"
          autocomplete="new-password"
          :error-message="passwordErrorMessage"
          prepend-icon="pi-lock"
          @validate="validatePassword"
        />

        <CustomInputText
          v-model="givenName"
          class="mb-4"
          :max-length="128"
          :label="t('Vorname')"
          autocomplete="given-name"
          prepend-icon="pi-id-card"
          :error-message="givenNameErrorMessage"
          @validate="validateGivenName"
        />

        <CustomInputText
          v-model="familyName"
          class="mb-4"
          :max-length="128"
          :label="t('Nachname')"
          autocomplete="family-name"
          prepend-icon="pi-id-card"
          :error-message="familyNameErrorMessage"
          @validate="validateFamilyName"
        />

        <CustomInputText
          v-model="zip"
          class="mb-4"
          :max-length="32"
          :label="t('Postleitzahl')"
          autocomplete="postal-code"
          prepend-icon="pi-map-marker"
          :error-message="zipErrorMessage"
          @validate="validateZip"
        />

        <Button
          type="submit"
          :loading="submittingVerification"
          :label="t('Verifizieren')"
          class="w-full mb-4"
        />

        <Button
          severity="contrast"
          outlined
          :label="t('Zurück')"
          class="w-full"
          @click="goBack"
        />
      </form>
    </div>
  </CustomCard>
</template>
